import React from 'react';
import { RotateCcw } from 'lucide-react';
import {
  Checkbox,
  Button,
  FilterToolbarSearch,
  VirtualizedMultiSelectCombobox,
  filterToolbarClinicClassName,
  filterToolbarControlMdClassName,
  filterToolbarSearchGrowClassName,
} from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import type { BillingTableQuery } from '../../hooks/useBillingTableQuery';
import type {
  BillingReadinessCategory,
  PayorType,
} from '../../services/api/types.gen';
import { PAYOR_TYPE_OPTIONS } from '../../services/clinicsApi';
import { useStore } from '../../store';
import { ClinicMultiSelect } from '../clinic/ClinicMultiSelect';
import { ExcludePreTakeoverFilterCheckbox } from '../filters/ExcludePreTakeoverFilterCheckbox';
import { useEOEClaimsManagerOptions } from './EOEClaimsManagerContext';
import { RegenerateClaimsButton } from './RegenerateClaimsButton';

/**
 * The fixed five-category failure grouping, in the order the product contract
 * lists them. Values are the API wire values of `BillingReadinessCategory`.
 */
const FAILING_CATEGORY_OPTIONS: {
  value: BillingReadinessCategory;
  label: string;
}[] = [
  { value: 'oasis', label: 'OASIS' },
  { value: 'hipps', label: 'HIPPS' },
  { value: 'visits', label: 'Visits' },
  { value: 'orders', label: 'Orders' },
  { value: 'insurance', label: 'Insurance' },
];

interface Props {
  query: BillingTableQuery;
  onReset: () => void;
  /** Render the Not Ready failure-category filter. Off for every other tab. */
  showFailingCategories?: boolean;
}

export function BillingTableToolbar({
  query,
  onReset,
  showFailingCategories = false,
}: Props) {
  const { insurances, clinics } = useEOEClaimsManagerOptions();
  const {
    agencyClinics,
    fetchAgencyClinics,
    hideTestPatients,
    setHideTestPatients,
    showInactiveEpisodes,
    setShowInactiveEpisodes,
    showNothingToBill,
    setShowNothingToBill,
    hideImportedPatients,
    setHideImportedPatients,
    excludePreTakeover,
    setExcludePreTakeover,
  } = useStore(
    useShallow(state => ({
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
      // Page-wide (not per-tab): every tab's toolbar reads the same slice,
      // so toggling it here carries to the other tabs, and the choice
      // persists through the store's `persist` middleware.
      hideTestPatients: state.hideTestPatients,
      setHideTestPatients: state.setHideTestPatients,
      showInactiveEpisodes: state.showInactiveEpisodes,
      setShowInactiveEpisodes: state.setShowInactiveEpisodes,
      showNothingToBill: state.showNothingToBill,
      setShowNothingToBill: state.setShowNothingToBill,
      hideImportedPatients: state.hideImportedPatients,
      setHideImportedPatients: state.setHideImportedPatients,
      excludePreTakeover: state.excludePreTakeover !== false,
      setExcludePreTakeover: state.setExcludePreTakeover,
    }))
  );

  React.useEffect(() => {
    if (agencyClinics.length === 0) {
      void fetchAgencyClinics();
    }
  }, [agencyClinics.length, fetchAgencyClinics]);

  const clinicSelectClinics = React.useMemo(
    () =>
      clinics.map(c => {
        const full = agencyClinics.find(ac => ac.id === c.id);
        return {
          id: c.id,
          name: c.name,
          clinic_source: full?.clinic_source,
        };
      }),
    [clinics, agencyClinics]
  );

  // One option per payer name (case and edge spaces ignored); it covers every
  // clinic's insurance row with that name. The stored filter stays a list of ids.
  const insuranceGroups = React.useMemo(() => {
    const groups = new Map<string, { label: string; ids: string[] }>();
    for (const i of insurances) {
      const key = i.name.trim().toLowerCase();
      const group = groups.get(key);
      if (group) group.ids.push(i.id);
      else groups.set(key, { label: i.name, ids: [i.id] });
    }
    return groups;
  }, [insurances]);
  const insuranceOptions = React.useMemo(
    () =>
      [...insuranceGroups].map(([key, g]) => ({ value: key, label: g.label })),
    [insuranceGroups]
  );
  // Stored ids that match no option stay selected under their own id, so a
  // filter that still applies is visible and can be cleared.
  const selectedInsuranceKeys = React.useMemo(() => {
    const grouped = new Set([...insuranceGroups.values()].flatMap(g => g.ids));
    return [
      ...[...insuranceGroups]
        .filter(([, g]) => g.ids.some(id => query.insuranceIds.includes(id)))
        .map(([key]) => key),
      ...query.insuranceIds.filter(id => !grouped.has(id)),
    ];
  }, [insuranceGroups, query.insuranceIds]);
  // A stored filter can hold only some ids of a payer name. Widen it to the
  // whole group, so the rows match the checked option.
  const { insuranceIds, setInsuranceIds } = query;
  React.useEffect(() => {
    const widened = [
      ...new Set(
        insuranceIds.flatMap(id => {
          for (const g of insuranceGroups.values())
            if (g.ids.includes(id)) return g.ids;
          return [id];
        })
      ),
    ];
    if (widened.length !== insuranceIds.length) setInsuranceIds(widened);
  }, [insuranceGroups, insuranceIds, setInsuranceIds]);

  // Local input state keeps typing responsive; the committed search is debounced
  // (~250ms) so each keystroke does not trigger a server round-trip.
  const [searchValue, setSearchValue] = React.useState(query.search);

  React.useEffect(() => {
    setSearchValue(query.search);
  }, [query.search]);

  React.useEffect(() => {
    if (searchValue === query.search) return;
    const handle = setTimeout(() => query.setSearch(searchValue), 250);
    return () => clearTimeout(handle);
  }, [searchValue, query.search, query.setSearch]);

  return (
    <div className='mb-3 flex flex-col gap-3'>
      {/* Search uses the shared labeled control; comboboxes stay placeholder-only. */}
      <div className='grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-end'>
        <FilterToolbarSearch
          value={searchValue}
          onChange={setSearchValue}
          label='Search'
          placeholder='Search...'
          containerClassName={`${filterToolbarSearchGrowClassName} col-span-2`}
          className='w-full'
        />

        <VirtualizedMultiSelectCombobox
          options={insuranceOptions}
          value={selectedInsuranceKeys}
          onValueChange={keys =>
            query.setInsuranceIds(
              keys.flatMap(k => insuranceGroups.get(k)?.ids ?? [k])
            )
          }
          placeholder='All Insurances'
          searchPlaceholder='Search insurances…'
          buttonClassName={`h-8 ${filterToolbarControlMdClassName}`}
        />
        <VirtualizedMultiSelectCombobox
          options={PAYOR_TYPE_OPTIONS}
          value={query.payorTypes}
          onValueChange={values => query.setPayorTypes(values as PayorType[])}
          ariaLabel='Payor types'
          placeholder='All Payor Types'
          searchPlaceholder='Search payor types…'
          buttonClassName={`h-8 ${filterToolbarControlMdClassName}`}
        />
        <ClinicMultiSelect
          clinics={clinicSelectClinics}
          value={query.clinicIds}
          onValueChange={query.setClinicIds}
          placeholder='All Clinics'
          searchPlaceholder='Search clinics…'
          buttonClassName={`h-8 ${filterToolbarClinicClassName}`}
        />

        {showFailingCategories ? (
          <VirtualizedMultiSelectCombobox
            options={FAILING_CATEGORY_OPTIONS}
            value={query.failingCategories}
            // The combobox emits plain strings; its options are fixed to the five
            // category wire values, so every emitted value is a valid category.
            onValueChange={values =>
              query.setFailingCategories(values as BillingReadinessCategory[])
            }
            ariaLabel='Failing categories'
            placeholder='Any failing category'
            searchPlaceholder='Search categories…'
            buttonClassName={`h-8 ${filterToolbarControlMdClassName}`}
          />
        ) : null}
      </div>

      {/* Visibility toggles — own row so they don't run on with the selects. */}
      <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
        <div className='flex items-center gap-2'>
          <Checkbox
            id='billing-hide-test-patients'
            checked={hideTestPatients}
            onCheckedChange={(checked: boolean) => {
              setHideTestPatients(!!checked);
            }}
            checkboxSize='sm'
          />
          <label
            htmlFor='billing-hide-test-patients'
            className='text-xs text-muted-foreground cursor-pointer select-none'
          >
            Hide test patients
          </label>
        </div>
        <div className='flex items-center gap-2'>
          <Checkbox
            id='billing-show-inactive-episodes'
            checked={showInactiveEpisodes}
            onCheckedChange={(checked: boolean) => {
              setShowInactiveEpisodes(!!checked);
            }}
            checkboxSize='sm'
          />
          <label
            htmlFor='billing-show-inactive-episodes'
            className='text-xs text-muted-foreground cursor-pointer select-none'
          >
            Show inactive episodes
          </label>
        </div>
        <div className='flex items-center gap-2'>
          <Checkbox
            id='billing-show-nothing-to-bill'
            checked={showNothingToBill}
            onCheckedChange={(checked: boolean) => {
              setShowNothingToBill(!!checked);
            }}
            checkboxSize='sm'
          />
          <label
            htmlFor='billing-show-nothing-to-bill'
            className='text-xs text-muted-foreground cursor-pointer select-none'
          >
            Show cycles with nothing to bill
          </label>
        </div>
        <div className='flex items-center gap-2'>
          <Checkbox
            id='billing-hide-imported-patients'
            checked={hideImportedPatients}
            onCheckedChange={(checked: boolean) => {
              setHideImportedPatients(!!checked);
            }}
            checkboxSize='sm'
          />
          <label
            htmlFor='billing-hide-imported-patients'
            className='text-xs text-muted-foreground cursor-pointer select-none'
          >
            Filter out imported patients
          </label>
        </div>

        <ExcludePreTakeoverFilterCheckbox
          id='billing-exclude-pre-takeover'
          className='flex items-center gap-2'
          checked={excludePreTakeover}
          onCheckedChange={setExcludePreTakeover}
        />

        <Button
          variant='outline'
          size='sm'
          onClick={() => {
            // Clear the local input so a pending debounced search cannot re-apply
            // after reset (searchValue === '' === query.search => no reschedule).
            setSearchValue('');
            onReset();
          }}
          className='h-8'
        >
          <RotateCcw className='h-4 w-4' />
          Reset
        </Button>
      </div>

      <RegenerateClaimsButton />
    </div>
  );
}
